import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { IConfig } from '../config/interfaces';
import {
  X_PROMETHEUS_TRACE_ID_RESPONSE_HEADER,
  X_TRACE_ID_RESPONSE_HEADER,
} from '../utils/constant';
import EventEmitter from '../utils/eventEmitter';
import { storageGetItemWrapper } from '../utils/shared';

type AxiosRequestConfigType =
  | InternalAxiosRequestConfig<any>
  | (Promise<InternalAxiosRequestConfig<any>> & {
      headers: { Authorization: string };
      baseURL: string;
      params: any;
    });

type InjectAuthTokenType = (
  value: InternalAxiosRequestConfig<any>
) => InternalAxiosRequestConfig<any> | Promise<InternalAxiosRequestConfig<any>>;

export type OktaJwtPayload = JwtPayload & {
  auth_time: string;
  exp: string;
  uid: string;
};

export const axiosEvent = new EventEmitter();
const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage';

const formRequestUrl = (url: string): string => `https://${url}`;

const getConfigData = (): IConfig => {
  const persistRoot = JSON.parse(
    localStorage.getItem('persist:root') as string
  );
  return persistRoot
    ? JSON.parse(persistRoot?.config as string)
    : ({} as IConfig);
};
export const getLogForwarderAPIGWEndpoint = (): string => {
  const { data } = getConfigData();
  return data?.logForwarderAPIGWEndpoint?.url ?? '';
};

const logForwarderAPIGWClient = axios.create({
  baseURL: `${getLogForwarderAPIGWEndpoint()}`,
});

export const getBatchLimit = (): number => {
  const config = getConfigData();
  return config?.data?.loggerConfig.batchLimit ?? 5;
};

export const getBatchInterval = (): number => {
  const config = getConfigData();
  return config?.data?.loggerConfig.batchInterval ?? '';
};

export const getAuthToken: () => string = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  return oktaTokenStorage?.accessToken?.accessToken ?? '';
};

export const getUserId: () => string = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  return oktaTokenStorage?.accessToken?.claims?.uid ?? '';
};

export const getUserEmail: () => string = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  return oktaTokenStorage?.accessToken?.claims?.sub ?? '';
};

export const getPhanesBaseURL = (): string => {
  const { data } = getConfigData();
  const phanesBaseURL = data?.phanesDomain?.url;
  return phanesBaseURL ? formRequestUrl(phanesBaseURL) : '';
};

export const getBackendBaseURL = (): string => {
  const { data } = getConfigData();
  const prometheusBaseURL = data?.backendUrl?.url ?? '';
  return prometheusBaseURL ? formRequestUrl(prometheusBaseURL) : '';
};

export const getSaseBackendBaseURL = (): string => {
  const { data } = getConfigData();
  const saseBaseURL = data?.backendSaseUrl?.url ?? '';
  return saseBaseURL ? formRequestUrl(saseBaseURL) : '';
};

const phanesAPIClient = axios.create({
  baseURL: getPhanesBaseURL(),
  params: {
    domain: 'phanes',
  },
});

const prometheusAPIClient = axios.create({
  baseURL: getBackendBaseURL(),
  params: {
    domain: 'prometheus',
  },
});

const SASE_DOMAIN = 'sase';
const sdwanCatalystAPIClient = axios.create({
  baseURL: getSaseBackendBaseURL(),
  params: {
    domain: SASE_DOMAIN,
  },
});

const apiClients = [
  phanesAPIClient,
  prometheusAPIClient,
  sdwanCatalystAPIClient,
];

export const injectRequestData: InjectAuthTokenType = async (
  config: AxiosRequestConfigType
) => {
  const authToken = getAuthToken();
  if (!authToken) {
    return await config;
  }
  if (config.baseURL?.length === 0) {
    const { data: configData } = await axios.get('/config/config.json');
    if (config.params?.domain === 'phanes') {
      config.baseURL = formRequestUrl(configData.phanesDomain.url);
    } else if (config.params?.domain === SASE_DOMAIN) {
      config.baseURL = formRequestUrl(configData.backendSaseUrl.url);
    } else {
      config.baseURL = formRequestUrl(configData.backendUrl.url);
    }
  }
  config.params = null;
  config.headers.Authorization = `Bearer ${authToken}`;
  return await config;
};

export const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};
export const onResponseError = async (error: any): Promise<void> => {
  if (error?.response?.status === 401) {
    const loggerObject = getLoggerObjectFromError(error);
    axiosEvent.emit('axios_401', loggerObject);
  }
  throw error;
};

export const getLoggerObjectFromError = (error: AxiosError): any => {
  const originalRequest = error.config ?? ({} as AxiosRequestConfig);
  const decodedJwt: OktaJwtPayload = jwtDecode(
    (originalRequest.headers?.Authorization ?? '')?.split(' ')[1]
  );
  const { auth_time = '', exp, uid = '' } = decodedJwt;
  const currentTimeStamp = Date.now();
  const traceId: string =
    error?.response?.headers[X_TRACE_ID_RESPONSE_HEADER] ??
    error?.response?.headers[X_PROMETHEUS_TRACE_ID_RESPONSE_HEADER] ??
    '';
  return {
    auth_time,
    exp,
    uid,
    currentTimeStamp,
    traceId,
  };
};

apiClients.forEach((apiClient) => {
  apiClient.interceptors.request.use(injectRequestData);
  apiClient.interceptors.response.use(onResponse, onResponseError);
});

export {
  phanesAPIClient,
  prometheusAPIClient,
  sdwanCatalystAPIClient,
  logForwarderAPIGWClient,
};

export const isSccAdmin: () => string = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  const roles = oktaTokenStorage?.accessToken?.claims?.['security-cloud'] ?? [];
  const hasAdmin = roles.some((role: string) => role.includes('admin'));
  return hasAdmin;
};

export const isSccReadOnly: () => boolean = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  const securityCloudData =
    oktaTokenStorage?.accessToken?.claims?.['security-cloud'] ?? [];
  const hasReadOnlyRole = securityCloudData.includes(
    'security:enterprise:readonly'
  );
  return hasReadOnlyRole;
};

export const isSccEnterpriseAdmin: () => boolean = () => {
  const oktaTokenStorage = JSON.parse(
    storageGetItemWrapper(OKTA_TOKEN_STORAGE_KEY) as string
  );
  const securityCloudData =
    oktaTokenStorage?.accessToken?.claims?.['security-cloud'] ?? [];
  const isAdmin = securityCloudData.some((entry: string) =>
    entry.includes('security:enterprise:admin')
  );
  return isAdmin;
};
